import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IconCalendar, IconXClose } from "@pentabd/icons";
import { Button, InputDate, Table, Text } from "@pentabd/ui";

import { VendorsTableColumn } from "./VendorsTableColumn";

import { useVendorList } from "@hooks/procurements/vendors/useVendors";
import { VendorsResponseType } from "@type/procurements/vendor";
import { Pagination } from "@type/procurements/pagination";
import useActionCreator from "@redux/hooks/useActionCreator";
import { DeleteConfirmationModal, ErrorModal, SuccessModal } from "@components";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import SearchController from "@components/SearchController";

import { TablePagination } from "@helpers/tablePagination";

export default function VendorsListTable() {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [itemId, setItemId] = useState<number>();
  const [itemName, setItemName] = useState<string>("");

  const { t } = useTranslation();
  const { control } = useForm();
  const navigate = useNavigate();
  const { vendorDeleteActionHandler } = useActionCreator();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { handleQuery, vendorState, ClearQueries, query } = useVendorList();

  const { getData } = vendorState;

  const { data, pagination } = getData || ({} as VendorsResponseType);
  const { page, total } = pagination || ({} as Pagination);

  const { deleteSuccess, deleteError } = useCustomSelector(
    (store) => store.procurements.vendorState,
  );

  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    handleQuery("page", page);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const closeConfirmationModel = () => setIsConfirmationModalOpen(false);

  const openConfirmationModel = (itemName: string, id: string | number) => {
    setItemId(id as number);
    setItemName(itemName);
    setIsConfirmationModalOpen(true);
  };

  const deleteAction = () => {
    closeConfirmationModel();
    vendorDeleteActionHandler(itemId as number);
  };

  useEffect(() => {
    if (deleteSuccess) {
      setIsSuccessModalOpen(true);
    } else if (deleteError) {
      setIsErrorModalOpen(true);
    }
  }, [deleteSuccess, deleteError]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setIsConfirmationModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <div className="pt-10">
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={
            !!deleteError
              ? deleteError?.response?.data?.message
              : "Something went wrong"
          }
        />
      )}
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          subtitle={itemName}
          isModalOpen={isConfirmationModalOpen}
          onClose={closeConfirmationModel}
          action={deleteAction}
        />
      )}

      {/* modal ends */}
      <Table
        headerExtension={{
          leftComponents: [
            <div className="row">
              <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                <Text
                  weight="semibold"
                  color="title"
                  size="sm"
                  component="p"
                  className="mb-3"
                >
                  {t("PROCUREMENT_VENDORS_TABLE.TABLE.START_DATE_FILTER")}
                </Text>
                <InputDate
                  name={"startDate"}
                  size="sm"
                  onSelectDate={(date) => {
                    handleQuery("startDate", date);
                  }}
                  // defaultValue={query?.startDate as string}
                  placeholder={t(
                    "PROCUREMENT_VENDORS_TABLE.TABLE.START_DATE_FILTER",
                  )}
                  prefix={<IconCalendar size="20" fill="subtitle2" />}
                  portal={true}
                />
              </div>
              <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                <Text
                  weight="semibold"
                  color="title"
                  size="sm"
                  component="p"
                  className="mb-3"
                >
                  {t("PROCUREMENT_VENDORS_TABLE.TABLE.END_DATE_FILTER")}
                </Text>
                <InputDate
                  name={"startDate"}
                  size="sm"
                  onSelectDate={(date) => {
                    handleQuery("endDate", date);
                  }}
                  placeholder={t(
                    "PROCUREMENT_VENDORS_TABLE.TABLE.END_DATE_FILTER",
                  )}
                  prefix={<IconCalendar size="20" fill="subtitle2" />}
                  portal={true}
                />
              </div>

              <div className="col-12 col-lg-4 col-xl-3 m-0 pt-5 px-5">
                <Text
                  weight="semibold"
                  color="title"
                  size="sm"
                  component="p"
                  className="mb-3"
                >
                  {t("TM_ARCHIVED_TASKS.TASKS_LIST.TABLE.SEARCH_CLEAR")}
                </Text>
                <Button
                  fill="outline"
                  type="warning"
                  size="sm"
                  onClick={() => {
                    ClearQueries();
                  }}
                >
                  <IconXClose size="18" fill="warning" />
                </Button>
              </div>
            </div>,
          ],
        }}
        header={{
          title: t("PROCUREMENT_VENDORS_TABLE.TABLE.LABEL"),
          badge: {
            label: `${total}`,
            size: "sm",
            type: "primary",
          },

          buttons: [
            <SearchController
              control={control}
              name="search"
              onChange={(e) => {
                handleQuery("search", e);
              }}
              value={query?.search}
            />,
          ],
        }}
        columns={VendorsTableColumn(t, navigate, openConfirmationModel)}
        // edit={{
        //   customIcon: <IconEdit01 size={24} fill="subtitle1" />,
        //   onClick: (id) => {
        //     navigate(`${id}`);
        //   },
        // }}
        // remove={{
        //   customIcon: <IconTrash01 size={24} fill="danger" />,
        //   onClick: (id) => {
        //     vendorDeleteActionHandler(id);
        //   },
        // }}
        rows={data || []}
        pagination={TablePagination(pagination, handleQuery)}
      />
    </div>
  );
}
